import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, Navigate, useNavigate, useParams } from "react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AudioPlayer,
  Button,
  Card,
  EmptyState,
  Icon,
  LevelMeter,
  ProgressBar,
  RecordButton,
  SegmentedControl,
  Skeleton,
  Stepper,
  Tag,
  Waveform,
  useToast,
} from "@xinghai/ui";
import type { AudioPlayerHandle, Step } from "@xinghai/ui";
import { studentApi, uploadRecording } from "../../lib/api";
import type { ClientMeta } from "../../lib/api";
import { qk } from "../../lib/queries";
import { getSessionId } from "../../lib/audio/session";
import { useRecorder } from "../../lib/audio/useRecorder";
import type { RecorderResult } from "../../lib/audio/recorder";
import { MAX_DURATION_MS } from "../../lib/audio/recorder";
import { gradeNoise, readNoiseCheck, snrDb } from "../../lib/audio/noiseCheck";
import type { NoiseCheck } from "../../lib/audio/noiseCheck";
import { isWeChatBrowser } from "../../lib/ua";
import { buzz } from "../../lib/haptics";
import { useSettings } from "../../lib/settings";
import { enqueue, isRetriableUploadError, newUploadKey } from "../../lib/queue";
import { useOnline } from "../../app/useOnline";
import { useUploadQueueBridge } from "../../app/useUploadQueueBridge";
import { PendingUploads } from "./PendingUploads";
import { MaterialDisplay, splitSentences } from "./MaterialDisplay";
import { SkipSheet } from "./SkipSheet";
import type { SkipReason } from "./SkipSheet";
import { NoiseCheckSheet } from "./NoiseCheckSheet";
import { WeChatNotice } from "./WeChatNotice";
import { MicHelp } from "./MicHelp";
import { RecordingTipsSheet, markTipsSeen, tipsSeen } from "./RecordingTipsSheet";
import { Illustration } from "../shared/Illustration";
import { TASK_ICONS, TASK_LABEL } from "./taskMeta";
import { assignmentHref } from "./TasksPage";
import { aiTestSectionCaption, levelOf, nextInGroup, nextUp, stageSummaries } from "./grouping";

/**
 * A pause-heavy sentence can legitimately have a high silent-frame ratio.
 * Gate on signal evidence instead: a take is suspicious only when both its
 * peak and whole-take RMS are below the cross-device floor. This avoids
 * rejecting quiet speech from Firefox/mobile microphones just because the
 * student paused between syllables.
 */
const SILENCE_PEAK_WARN = 0.02;
const SILENCE_RMS_WARN = 0.0025;
/** The 撤销 window before an automatic submit goes out. */
const UNDO_MS = 3000;

type Pass = "slow" | "normal";

/** What the screen is doing right after a take. */
type Phase = "idle" | "holding" | "review" | "silence";

interface UploadVars {
  take: RecorderResult;
  meta: ClientMeta;
  forced: boolean;
  /**
   * docs/ROADMAP.md B2 — minted once per take and kept across every retry,
   * including the ones the offline queue makes later, so the same recording can
   * never be created twice.
   */
  uploadKey: string;
}

interface DoneState {
  kind: "level" | "stage" | "session";
  title: string;
  detail: string;
  nextHref?: string;
}

/** docs/UX.md §1.3 — 录音流 = 两次点击一条. */
export function RecordPage() {
  const { assignmentId = "" } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const qc = useQueryClient();
  const online = useOnline();
  const [settings] = useSettings();
  // B2 — the queue belongs to this student, and retries run while this screen is open.
  useUploadQueueBridge();

  const q = useQuery({ queryKey: qk.assignments, queryFn: () => studentApi.assignments() });
  const items = useMemo(() => q.data?.assignments ?? [], [q.data]);
  const assignment = items.find((a) => a.id === assignmentId) ?? null;

  const [pass, setPass] = useState<Pass>("slow");
  const [donePasses, setDonePasses] = useState<ReadonlySet<Pass>>(new Set());
  const [segment, setSegment] = useState(0);
  const [doneSegments, setDoneSegments] = useState<ReadonlySet<number>>(new Set());
  const [progress, setProgress] = useState<number | null>(null);
  const [skipOpen, setSkipOpen] = useState(false);
  const [done, setDone] = useState<DoneState | null>(null);
  const [phase, setPhase] = useState<Phase>("idle");
  const [interrupted, setInterrupted] = useState(false);
  // docs/ROADMAP.md A7 — one room-noise probe per browsing session.
  const [noise, setNoise] = useState<NoiseCheck | null>(() => readNoiseCheck());
  const [noiseOpen, setNoiseOpen] = useState(() => readNoiseCheck() === null);
  // 录音小贴士: once per device, before the first take.
  const [tipsOpen, setTipsOpen] = useState(() => !tipsSeen());

  /**
   * §9 hard stop. The default is 22 s, but a material may carry its own cap
   * (气息类：持续发 si、一口气绕口令，最多 45 s) and the server enforces exactly
   * the same number on upload.
   */
  const maxMs = assignment?.material?.max_duration_ms ?? MAX_DURATION_MS;
  /** A longer cap means one long breath — the student gets a picture of it. */
  const isBreathItem = maxMs > MAX_DURATION_MS;
  /**
   * T3 慢速 + 正常两遍 is the P0 corpus rule. 《AI 正音测试》 sets the material's
   * `passes` to 1, which arrives as `required_count: 1` — one take, no pass picker.
   */
  const twoPass = assignment?.task === "T3_tongue_twister" && (assignment.required_count ?? 2) >= 2;

  /** How a queued take names itself in 「待上传」 — never a raw id. */
  const queueTitle = assignment
    ? `${TASK_LABEL[assignment.task]} · ${assignment.material?.text ?? assignment.material_id}`
    : "录音";

  const recorder = useRecorder({ maxMs });
  const { reset: resetRecorder } = recorder;
  const holdRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const referenceRef = useRef<AudioPlayerHandle>(null);

  // A new assignment always starts from a clean slate.
  useEffect(() => {
    resetRecorder();
    setPass("slow");
    setDonePasses(new Set());
    setSegment(0);
    setDoneSegments(new Set());
    setProgress(null);
    setPhase("idle");
    setInterrupted(false);
    setDone(null);
  }, [assignmentId, resetRecorder]);

  const sentences = useMemo(
    () => (assignment ? splitSentences(assignment.material) : []),
    [assignment],
  );

  /**
   * Auto-advance stays inside the same group; crossing a 关 (T2) or finishing
   * the stage shows a completion screen instead of silently jumping on.
   */
  const goNextAssignment = useCallback(() => {
    const stage = stageSummaries(items).find((s) => s.items.some((a) => a.id === assignmentId));
    const next = nextInGroup(items, assignmentId);
    if (next) {
      if (
        stage?.kind === "task" &&
        stage.task === "T2_diagnostic" &&
        levelOf(stage, next.id) !== levelOf(stage, assignmentId)
      ) {
        const finished = levelOf(stage, assignmentId);
        const totalLevels = stage.levelCount ?? finished;
        setDone({
          kind: "level",
          title: `第 ${finished} 关完成`,
          detail:
            totalLevels > finished ? `还剩 ${totalLevels - finished} 关。` : "这一项都录完了。",
          nextHref: assignmentHref(next),
        });
        return;
      }
      void navigate(assignmentHref(next));
      return;
    }
    const other = nextUp(items.filter((a) => a.id !== assignmentId));
    if (other) {
      setDone({
        kind: "stage",
        title: `${stage?.label ?? "这一组"}录完了`,
        detail: `接下来是${other.stage.label}。`,
        nextHref: assignmentHref(other.assignment),
      });
      return;
    }
    setDone({ kind: "session", title: "今天辛苦了", detail: "今天安排的录音都完成了。" });
  }, [items, assignmentId, navigate]);

  /**
   * What happens after a take is *accounted for* — whether it went straight to
   * the server or into the offline queue. docs/ROADMAP.md B2 是这样定的：录完
   * 就往下走，补传是后台的事，学生不用站在原地等网络。
   */
  const advanceAfterTake = () => {
    resetRecorder();
    setPhase("idle");
    if (!assignment) return;
    if (twoPass) {
      const nextDone = new Set(donePasses).add(pass);
      setDonePasses(nextDone);
      const other: Pass = pass === "slow" ? "normal" : "slow";
      if (!nextDone.has(other)) {
        // docs/UX.md §1.3 — 第一遍录完自动切到第二遍
        setPass(other);
        toast.info(other === "normal" ? "慢速录好了，接着用正常语速再来一遍。" : "再录一遍慢速。");
        return;
      }
    } else if (assignment.task === "T4_passage") {
      const nextDone = new Set(doneSegments).add(segment);
      setDoneSegments(nextDone);
      const nextIdx = sentences.findIndex((_, i) => !nextDone.has(i));
      if (nextIdx >= 0) {
        setSegment(nextIdx);
        return;
      }
    }
    goNextAssignment();
  };

  const upload = useMutation({
    /**
     * docs/ROADMAP.md B2 — TanStack's default `networkMode: "online"` *pauses* a
     * mutation while `navigator.onLine` is false, so the submit would sit there
     * and nothing would ever reach the offline queue. Being offline is exactly
     * the case this screen has to handle, so it always runs.
     */
    networkMode: "always",
    mutationFn: async (vars: UploadVars) => {
      if (!assignment) throw new Error("assignment missing");
      setProgress(0);
      const input = {
        assignment_id: assignment.id,
        session_id: getSessionId(),
        elicitation: "natural" as const,
        duration_ms: vars.take.durationMs,
        sample_rate: vars.take.sampleRate,
        blob: vars.take.blob,
        filename: `${assignment.id}.wav`,
        client_meta: vars.meta,
        upload_key: vars.uploadKey,
      };
      const queueIt = () =>
        enqueue({
          uploadKey: vars.uploadKey,
          assignmentId: assignment.id,
          sessionId: input.session_id,
          title: queueTitle,
          task: assignment.task,
          durationMs: input.duration_ms,
          sampleRate: input.sample_rate,
          clientMeta: vars.meta,
          blob: input.blob,
          filename: input.filename,
        });

      // B2 — no network at all: skip the doomed request and queue it right away.
      if (!online) return { queued: await queueIt() };
      try {
        await uploadRecording(input, setProgress);
        return { queued: null };
      } catch (err) {
        // A 4xx means the take itself is wrong (太长 / 授权未签). Queuing that
        // would only fail forever — it stays here, where the student can act.
        if (!isRetriableUploadError(err)) throw err;
        return { queued: await queueIt() };
      }
    },
    onSuccess: (outcome) => {
      setProgress(null);
      void qc.invalidateQueries({ queryKey: qk.assignments });
      void qc.invalidateQueries({ queryKey: qk.recordings });
      if (outcome.queued && !outcome.queued.ok) {
        // The device refused to store it — the take is still in memory, so the
        // student has to stay here and retry rather than walk away and lose it.
        toast.error(
          outcome.queued.reason === "quota"
            ? "手机存不下了。先别离开这个页面，网络恢复后点「再试一次」。"
            : "这个浏览器存不了离线录音。先别离开这个页面，网络恢复后点「再试一次」。",
        );
        setPhase("review");
        return;
      }
      if (outcome.queued) {
        toast.info("这条先存在手机里了，联网后会自动补传。接着录下一条吧。");
      }
      advanceAfterTake();
    },
    onError: () => {
      setProgress(null);
      setPhase("review");
    },
  });

  const skip = useMutation({
    mutationFn: ({ reason, note }: { reason: SkipReason; note: string }) =>
      studentApi.skip(assignmentId, reason, note || undefined),
    onSuccess: () => {
      setSkipOpen(false);
      void qc.invalidateQueries({ queryKey: qk.assignments });
      toast.info("已跳过这条，老师会看到你的说明。");
      goNextAssignment();
    },
    onError: () => toast.error("跳过没有成功，请重试。"),
  });

  const submitTake = useCallback(
    (take: RecorderResult, forced: boolean) => {
      if (!assignment) return;
      const snr = noise ? snrDb(take.rms, noise.rms) : null;
      const meta: ClientMeta = {
        clipping: take.clipping,
        clip_count: take.clipCount,
        silence_ratio: Number(take.silenceRatio.toFixed(4)),
        peak: Number(take.peak.toFixed(4)),
        forced,
        user_agent: navigator.userAgent,
        ...(noise ? { noise_floor_rms: Number(noise.rms.toFixed(6)) } : {}),
        ...(snr !== null ? { snr_db: snr } : {}),
        ...(twoPass ? { pass } : {}),
        ...(assignment.task === "T4_passage" ? { segment_index: segment } : {}),
      };
      // B2 — one key per take; 「再试一次」 and every queued retry reuse it.
      upload.mutate({ take, meta, forced, uploadKey: newUploadKey() });
    },
    [assignment, noise, pass, segment, twoPass, upload],
  );

  const cancelHold = useCallback(() => {
    if (holdRef.current !== null) {
      clearTimeout(holdRef.current);
      holdRef.current = null;
    }
    setPhase("review");
  }, []);

  const take = recorder.result;

  /**
   * After a take: silence blocks the submit, otherwise 自动提交 runs after a
   * 3 s 撤销 window (unless the student turned it off in 设置).
   */
  useEffect(() => {
    if (!take) return;
    if (take.peak < SILENCE_PEAK_WARN || take.rms < SILENCE_RMS_WARN) {
      setPhase("silence");
      return;
    }
    if (!settings.autoSubmit) {
      setPhase("review");
      return;
    }
    setPhase("holding");
    const timer = setTimeout(() => {
      holdRef.current = null;
      submitTake(take, take.clipping);
    }, UNDO_MS);
    holdRef.current = timer;
    const id = toast.show(
      <span className="xz-undo">
        正在提交…
        <button
          type="button"
          className="gz-button gz-button--quiet"
          onClick={() => {
            toast.dismiss(id);
            cancelHold();
          }}
        >
          撤销
        </button>
      </span>,
      { durationMs: UNDO_MS },
    );
    return () => {
      clearTimeout(timer);
      holdRef.current = null;
      toast.dismiss(id);
    };
    // `toast` and `submitTake` are stable enough; re-running on every take is
    // exactly what we want.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [take, settings.autoSubmit]);

  /** docs/UX.md §1.3 — 来电/切后台：停止并保留，回来后提示重录. */
  useEffect(() => {
    const onHidden = () => {
      if (document.visibilityState !== "hidden") return;
      if (recorder.status === "recording") {
        recorder.cancel();
        setInterrupted(true);
        setPhase("idle");
      }
    };
    document.addEventListener("visibilitychange", onHidden);
    return () => document.removeEventListener("visibilitychange", onHidden);
  }, [recorder]);

  /** docs/UX.md §1.3 — 演唱先播放本人的朗读版本作为参照. */
  const referenceUrl = assignment?.paired_audio_url ?? null;
  useEffect(() => {
    if (!referenceUrl || assignment?.task !== "T6_singing") return;
    const t = setTimeout(() => {
      try {
        referenceRef.current?.play();
      } catch {
        /* autoplay blocked — the student can press play */
      }
    }, 200);
    return () => clearTimeout(t);
  }, [referenceUrl, assignment?.task]);

  const handleToggle = useCallback(() => {
    buzz(12);
    if (recorder.status === "recording") void recorder.stop();
    else {
      setInterrupted(false);
      void recorder.start();
    }
  }, [recorder]);

  // docs/ROADMAP.md B1 — WeChat's WebView cannot record reliably.
  if (isWeChatBrowser()) return <WeChatNotice />;

  if (q.isPending) {
    return (
      <div className="xz-container xz-stack" aria-busy="true">
        <Skeleton variant="rect" height={180} />
        <Skeleton variant="rect" height={160} />
      </div>
    );
  }

  if (done) {
    return (
      <div className="xz-container xz-done">
        <EmptyState
          icon={<Illustration name="done-check" size="lg" />}
          title={
            <span>
              <Icon id="ui-check-circle" size={24} /> {done.title}
            </span>
          }
          description={done.detail}
          action={
            <div className="xz-done__actions">
              {done.nextHref && (
                <Button variant="primary" size="lg" onClick={() => void navigate(done.nextHref!)}>
                  继续
                </Button>
              )}
              <Link className="gz-button" to="/s">
                {done.nextHref ? "休息一下" : "回到首页"}
              </Link>
            </div>
          }
        />
      </div>
    );
  }

  if (!assignment) {
    return (
      <div className="xz-container">
        <EmptyState
          icon={<Icon id="ui-search" size={24} />}
          title="找不到这条任务"
          description="它可能已经完成或被撤回了。"
          action={
            <Link className="gz-button gz-button--primary" to="/s">
              回到首页
            </Link>
          }
        />
      </div>
    );
  }

  if (assignment.task === "T7_perception") {
    return <Navigate to={`/s/listen/${assignment.id}`} replace />;
  }

  const pairItems = assignment.pair_group
    ? items
        .filter((a) => a.pair_group === assignment.pair_group)
        .sort((a, b) => (a.pair_step ?? 0) - (b.pair_step ?? 0))
    : [];

  const steps: Step[] = pairItems.map((a, i) => ({
    id: a.id,
    label: i === 0 ? "1 朗读" : "2 演唱",
    hint: a.state === "recorded" ? "已完成" : a.locked ? "先完成朗读" : undefined,
    done: a.state === "recorded",
    locked: Boolean(a.locked) && a.id !== assignment.id,
  }));
  const currentStep = Math.max(
    0,
    pairItems.findIndex((a) => a.id === assignment.id),
  );

  const recording = recorder.status === "recording";
  const busy = upload.isPending;
  const stage = stageSummaries(items).find((s) => s.items.some((a) => a.id === assignmentId));
  const totalTakes = assignment.required_count ?? 1;
  const doneTakes = twoPass
    ? donePasses.size
    : assignment.task === "T4_passage"
      ? doneSegments.size
      : 0;
  const positionLabel =
    stage?.kind === "task" && stage.task === "T2_diagnostic" && stage.levelCount
      ? `第 ${levelOf(stage, assignment.id)} 关 ${stage.done + 1}/${Math.min(30, stage.total)}`
      : `${doneTakes}/${totalTakes}`;
  /** 《AI星海正音》: 老师的条目标题，例如「17 发花辙 a ·《天净沙·秋思》 2/5」. */
  const sectionCaption = aiTestSectionCaption(items, assignment.id);

  return (
    <div className="xz-container xz-record-page xz-stack">
      <header className="xz-record-page__head">
        <Link className="gz-button gz-button--quiet" to="/s">
          <Icon id="ui-arrow-left" size={20} /> 返回
        </Link>
        <Tag tone="info">
          <Icon id={TASK_ICONS[assignment.task]} size={16} /> {TASK_LABEL[assignment.task]}
        </Tag>
        <span className="gz-caption">{positionLabel}</span>
      </header>

      {pairItems.length > 1 && (
        <Stepper
          steps={steps}
          current={currentStep}
          label="朗读与演唱两步"
          onStepClick={(i) => {
            const target = pairItems[i];
            if (target && target.id !== assignment.id) void navigate(`/s/task/${target.id}`);
          }}
        />
      )}

      <Card padding="md">
        {assignment.task === "T6_singing" && referenceUrl && (
          <div className="xz-reference">
            <p className="gz-caption">
              <Icon id="ui-play" size={16} /> 先听一下你自己刚才的朗读
            </p>
            <AudioPlayer
              ref={referenceRef}
              src={referenceUrl}
              hideRate
              size="sm"
              label="你的朗读版本"
            />
          </div>
        )}

        {isBreathItem && (
          <Illustration
            name="guide-breath"
            size="md"
            caption={`这条要一口气完成，先深吸一口气，最长 ${Math.round(maxMs / 1000)} 秒`}
          />
        )}

        {assignment.task === "T6_singing" && stage?.done === 0 && (
          <Illustration name="guide-singing" size="sm" caption="按熟悉的旋律唱，咬字清楚就好" />
        )}

        {sectionCaption && <p className="gz-caption xz-record-page__section">{sectionCaption}</p>}

        <MaterialDisplay
          material={assignment.material}
          task={assignment.task}
          segmentIndex={segment}
          doneSegments={doneSegments}
          onSelectSegment={
            recording || busy
              ? undefined
              : (i) => {
                  setSegment(i);
                  resetRecorder();
                  setPhase("idle");
                }
          }
        />

        {phase === "holding" && (
          <div className="xz-record-page__listen">
            <Button variant="secondary" onClick={cancelHold}>
              <Icon id="ui-play" size={20} /> 听一下
            </Button>
            <span className="gz-caption">不点就自动提交</span>
          </div>
        )}

        {twoPass && (
          <div className="xz-record-page__passes">
            <SegmentedControl<Pass>
              label="这一遍怎么读"
              size="lg"
              options={[
                { value: "slow", label: donePasses.has("slow") ? "慢速 ✓" : "慢速" },
                { value: "normal", label: donePasses.has("normal") ? "正常 ✓" : "正常" },
              ]}
              value={pass}
              onChange={(p) => {
                setPass(p);
                resetRecorder();
                setPhase("idle");
              }}
              disabled={recording || busy}
            />
          </div>
        )}
      </Card>

      {/* docs/ROADMAP.md B2 — 待上传 chip, also on 今日任务 and 我的录音. */}
      <PendingUploads />

      {!online && (
        <p className="gz-notice">
          <Icon id="ui-info" size={20} />
          现在没有网络，不影响你继续录。录音会先存在手机里，联网后自动补传。
        </p>
      )}

      {interrupted && (
        <p className="gz-notice" role="status">
          <Icon id="ui-info" size={20} />
          刚才被打断了，这条要重录一次。
        </p>
      )}

      {recorder.autoStopped && (
        <p className="gz-notice" role="status">
          <Icon id="ui-info" size={20} />到 {Math.round(maxMs / 1000)}{" "}
          秒上限自动停了。听一下，如果没读完就重录，说得紧凑一点。
        </p>
      )}

      {take?.clipping && (
        <p className="gz-notice gz-notice--error" role="status">
          <Icon id="ui-warning" size={20} />
          声音太大出现了破音。离麦克风远一点重录会更好；直接提交也可以。
        </p>
      )}

      {recorder.errorMessage ? (
        <MicHelp
          message={recorder.errorMessage}
          onRetry={() => {
            void recorder.start();
          }}
        />
      ) : phase === "silence" && take ? (
        <div className="xz-record-page__preview xz-stack">
          <p className="gz-notice gz-notice--error" role="alert">
            <Icon id="ui-warning" size={20} />
            好像没有录到声音，再来一次？靠近麦克风，正常音量读一遍就行。
          </p>
          <div className="xz-record-page__actions">
            <Button
              variant="primary"
              size="lg"
              block
              onClick={() => {
                resetRecorder();
                setPhase("idle");
              }}
            >
              <Icon id="ui-refresh" size={20} /> 再来一次
            </Button>
            <Button variant="ghost" loading={busy} onClick={() => submitTake(take, true)}>
              仍然提交
            </Button>
          </div>
        </div>
      ) : !take || phase === "holding" ? (
        <div className="xz-record-page__control">
          <RecordButton
            state={
              recording
                ? "recording"
                : recorder.status === "requesting" ||
                    noiseOpen ||
                    tipsOpen ||
                    phase === "holding" ||
                    busy
                  ? "disabled"
                  : "idle"
            }
            onToggle={handleToggle}
            elapsedMs={recorder.elapsedMs}
            maxMs={maxMs}
            size={112}
          />
          <LevelMeter
            rms={recorder.level.rms}
            peak={recorder.level.peak}
            clipped={recorder.level.clipped}
          />
          <p className="gz-caption">
            {recording
              ? "读完再点一下停止。"
              : phase === "holding"
                ? "正在提交，点「撤销」可以拦下来。"
                : "点一下开始录。"}
          </p>
        </div>
      ) : (
        <div className="xz-record-page__preview xz-stack">
          <Waveform
            peaks={recorder.peaks}
            durationSec={take.durationMs / 1000}
            height={80}
            label="刚才的录音波形"
          />
          {recorder.previewUrl && (
            <AudioPlayer src={recorder.previewUrl} hideRate label="试听刚才的录音" />
          )}
          {progress !== null && (
            <ProgressBar value={progress} label="上传进度" showLabel tone="info" />
          )}
          {upload.isError && (
            <p className="gz-notice gz-notice--error" role="alert">
              <Icon id="ui-error" size={20} />
              没能上传成功。录音还在，点「再试一次」就好。
            </p>
          )}
          <div className="xz-record-page__actions">
            <Button
              variant="secondary"
              size="lg"
              onClick={() => {
                resetRecorder();
                setPhase("idle");
              }}
              disabled={busy}
            >
              <Icon id="ui-refresh" size={20} /> 重录
            </Button>
            {upload.isError ? (
              <Button
                variant="primary"
                size="lg"
                block
                loading={busy}
                onClick={() => upload.variables && upload.mutate(upload.variables)}
              >
                再试一次
              </Button>
            ) : (
              <Button
                variant="primary"
                size="lg"
                block
                loading={busy}
                onClick={() => submitTake(take, take.clipping)}
              >
                提交
              </Button>
            )}
          </div>
        </div>
      )}

      {noise && (
        <p className="gz-caption xz-record-page__noise">
          <Icon id="ui-info" size={16} /> 环境检测：{gradeNoise(noise.rms).label}
          <Button variant="ghost" size="sm" onClick={() => setNoiseOpen(true)}>
            重新检测
          </Button>
        </p>
      )}

      <div className="xz-record-page__skip">
        <Button variant="ghost" onClick={() => setSkipOpen(true)} disabled={busy || recording}>
          跳过并说明原因
        </Button>
      </div>

      <RecordingTipsSheet
        open={tipsOpen && !noiseOpen}
        onClose={() => {
          markTipsSeen();
          setTipsOpen(false);
        }}
      />

      <NoiseCheckSheet
        open={noiseOpen}
        onDone={(check) => {
          setNoise(check);
          setNoiseOpen(false);
        }}
        onSkip={() => setNoiseOpen(false)}
      />

      <SkipSheet
        open={skipOpen}
        busy={skip.isPending}
        onClose={() => setSkipOpen(false)}
        onSubmit={(reason, note) => skip.mutate({ reason, note })}
      />
    </div>
  );
}
